/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ------------------------------------------------------------------
 * LOT-CUSTOM 2026-08-31 — product-card swatch sync + instant add-to-cart.
 * MOVED VERBATIM from snippets/product-item.liquid (md5 f0855b231e243ff91aa791865baa3327).
 * WHY: that <script> was rendered once PER CARD. Pages rendering N cards
 * registered N duplicate document-level listeners (16 on a normal PDP —
 * idempotent, so invisible, but 16x work per click). Pages that render ZERO
 * cards server-side got NO handler at all: the crest PDP's cards arrive via
 * <product-recommendations> fetch + innerHTML, and injected <script> never
 * executes, so its swatches had never worked. custom.js is loaded once on
 * every page (theme.liquid, defer), so one binding covers AJAX-injected
 * cards too. Do NOT move this back into a per-card snippet.
 * ------------------------------------------------------------------ */
(function () {
  document.addEventListener('change', function (event) {
    var swatch = event.target.closest && event.target.closest('.color-swatch__radio');
    if (!swatch) return;

    var productItem = swatch.closest('.product-item');
    if (!productItem) return;

    var btn = productItem.querySelector('.product-item__buy-now');
    var available = swatch.getAttribute('data-variant-available') === 'true';
    var variantId = swatch.getAttribute('data-variant-id');

    if (btn) {
      btn.disabled = !available;
      btn.textContent = available ? (btn.getAttribute('data-add-label') || 'Add to cart') : 'Sold out';
    }

    var imageWrapper = productItem.querySelector('.product-item__image-wrapper');
    if (imageWrapper) {
      imageWrapper.style.filter = available ? '' : 'grayscale(1)';
      imageWrapper.style.opacity = available ? '' : '0.55';
    }

    var infoWrapper = productItem.querySelector('.product-item__info');
    if (infoWrapper) {
      infoWrapper.style.opacity = available ? '' : '0.55';
    }

    var hiddenInput = productItem.querySelector('input[name="id"]');
    if (hiddenInput && variantId) {
      hiddenInput.value = variantId;
    }

    var mediaId = swatch.getAttribute('data-media-id');
    if (!mediaId) return;

    productItem.querySelectorAll('.product-item__primary-image').forEach(function (img) {
      img.hidden = img.getAttribute('data-media-id') !== mediaId;
    });
  });

  document.addEventListener('click', async function (event) {
    var btn = event.target.closest && event.target.closest('.product-item__buy-now');
    if (!btn || btn.disabled) return;

    event.preventDefault();

    var productItem = btn.closest('.product-item');
    if (!productItem) return;

    var hiddenInput = productItem.querySelector('input[name="id"]');
    var variantId = hiddenInput ? hiddenInput.value : null;
    if (!variantId) {
      console.error('No variant ID found for quick add');
      return;
    }

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    var miniCartDrawer = document.getElementById('mini-cart');
    if (miniCartDrawer) {
      miniCartDrawer.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      miniCartDrawer.removeAttribute('hidden');
      miniCartDrawer.setAttribute('aria-hidden', 'false');

      var cartToggle = document.querySelector(
        '[aria-controls="mini-cart"]:not([aria-expanded="true"]), [data-cart-toggle], .header__icon--cart'
      );
      if (cartToggle) cartToggle.click();
    }

    try {
      var addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });

      if (!addResponse.ok) throw new Error('Add to cart failed');
      await addResponse.json();

      var drawer = document.getElementById('mini-cart');
      if (drawer) {
        var sectionResponse = await fetch(window.location.pathname + '?section_id=mini-cart');
        var sectionHtml = await sectionResponse.text();
        var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var newDrawer = doc.getElementById('mini-cart');
        if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
      }

      var cartCountEls = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count');
      if (cartCountEls.length) {
        var cartResponse = await fetch('/cart.js');
        var cartData = await cartResponse.json();
        cartCountEls.forEach(function (el) {
          el.textContent = cartData.item_count;
          el.hidden = cartData.item_count === 0;
        });
      }

      btn.textContent = 'Added!';
      setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);
    } catch (error) {
      console.error(error);
      btn.textContent = 'Error, try again';
      setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);
    }
  });
})();
